import { useEffect, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Slider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  Typography,
} from '@mui/material';
import { Formik, Form, useField, useFormikContext } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserRole } from '@/types/user';
import { useAMC } from '../context/AMCContext';
import { FormikTextField } from '../components/FormikTextField';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import { FormikSwitch } from '../components/FormikSwitch';
import {
  removeNullOrUndefined,
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
} from '@/services';
import { Asset, useGetIpSuggestionsQuery } from '@/services/assetApi';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  // webhookUrl: yup.string().url('Must be a valid URL').optional(),
  callbackSuccess: yup.string().optional().url('Must be a valid URL'),
  callbackFailure: yup.string().url('Must be a valid URL'),
  insertDtr: yup.boolean(),
  insertAotm: yup.boolean(),
  webhookTimeout: yup
    .number()
    .min(1, 'Timeout must be at least 1 second')
    .max(60, 'Timeout cannot exceed 60 seconds')
    .nullable(),
  webhookTimeoutEnabled: yup.boolean(),
  retryEnabled: yup.boolean(),
  maxRetry: yup
    .number()
    .min(0, 'Must be at least 0')
    .max(5, 'Cannot exceed the universal maximum of 5')
    .integer('Must be a whole number'),
  // upiAutopayPendingHtml: yup.string().optional(),
});

type FormValues = Omit<Asset, 'id' | 'amc'> & {
  webhookTimeoutEnabled: boolean;
};

const initialValues: FormValues = {
  name: '',
  apiKey: '',
  callbackSuccess: '',
  callbackFailure: '',
  insertDtr: false,
  insertAotm: false,
  upiAutopayPendingHtml: '',
  allowedIps: '',
  webhookTimeout: 1,
  webhookTimeoutEnabled: false,
  upiTimeout: 300,
  retryEnabled: false,
  maxRetry: 3,
};

const UPI_TIMEOUT_PRESETS = [300, 600, 900, 1200, 1500, 1800] as const;
const DEFAULT_UPI_TIMEOUT: (typeof UPI_TIMEOUT_PRESETS)[number] = UPI_TIMEOUT_PRESETS[0];

const toUpiTimeoutNumber = (raw: unknown): number => {
  if (raw === null || raw === undefined || raw === '') return DEFAULT_UPI_TIMEOUT;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return DEFAULT_UPI_TIMEOUT;
  const truncated = Math.trunc(parsed);
  return (UPI_TIMEOUT_PRESETS as readonly number[]).includes(truncated)
    ? truncated
    : DEFAULT_UPI_TIMEOUT;
};

const formatSecondsAsMinutes = (seconds: number): string => {
  const minutes = seconds / 60;
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

const UpiTimeoutField = () => {
  const [field, meta, helpers] = useField<number>('upiTimeout');
  const isPresetValue =
    typeof field.value === 'number' &&
    (UPI_TIMEOUT_PRESETS as readonly number[]).includes(field.value);
  const selectValue = String(isPresetValue ? field.value : DEFAULT_UPI_TIMEOUT);
  const showError = meta.touched && Boolean(meta.error);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    helpers.setValue(Number(event.target.value));
    helpers.setTouched(true, false);
  };

  return (
    <FormControl variant="filled" fullWidth error={showError}>
      <InputLabel id="upi-timeout-label">UPI Timeout</InputLabel>
      <Select
        labelId="upi-timeout-label"
        label="UPI Timeout"
        value={selectValue}
        onChange={handleSelectChange}
      >
        {UPI_TIMEOUT_PRESETS.map((preset) => (
          <MenuItem key={preset} value={String(preset)}>
            {formatSecondsAsMinutes(preset)}
          </MenuItem>
        ))}
      </Select>
      {showError ? <FormHelperText>{meta.error}</FormHelperText> : null}
    </FormControl>
  );
};

const WebhookTimeoutField = () => {
  const [enabledField, , enabledHelpers] = useField<boolean>('webhookTimeoutEnabled');
  const [timeoutField, timeoutMeta, timeoutHelpers] = useField<number>('webhookTimeout');
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const sliderValue = timeoutField.value ?? 1;
  const hasError = enabledField.value && !!timeoutMeta.error;

  const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!values.webhookUrl) {
      enabledHelpers.setValue(false);
      return;
    }
    enabledHelpers.setValue(checked);
    if (checked && (timeoutField.value === undefined || timeoutField.value < 1)) {
      timeoutHelpers.setValue(1);
    }
  };

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    const numericValue = Array.isArray(value) ? value[0] : value;
    timeoutHelpers.setValue(Math.round(numericValue));
    timeoutHelpers.setTouched(true, true);
  };

  useEffect(() => {
    if (!values.webhookUrl) {
      if (enabledField.value) {
        setFieldValue('webhookTimeoutEnabled', false, false);
      }
      if (timeoutField.value !== 1) {
        setFieldValue('webhookTimeout', 1, false);
      }
    }
  }, [values.webhookUrl, enabledField.value, timeoutField.value, setFieldValue]);

  return (
    <FormControl fullWidth error={hasError} sx={{ mt: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabledField.value && !!values.webhookUrl}
            onChange={handleToggle}
            disabled={!values.webhookUrl}
          />
        }
        label="Wait for Webhook Response"
      />
      <Box sx={{ pl: 4, pt: 1 }}>
        <Slider
          min={1}
          max={60}
          step={1}
          marks={[
            { value: 1, label: '1s' },
            { value: 60, label: '60s' },
          ]}
          value={enabledField.value && values.webhookUrl ? sliderValue : 1}
          disabled={!enabledField.value || !values.webhookUrl}
          valueLabelDisplay="auto"
          onChange={handleSliderChange}
        />
        <Typography variant="caption" color="text.secondary">
          {!values.webhookUrl
            ? 'Add a webhook URL to configure timeout'
            : enabledField.value
              ? `Maximum Webhook timeout: ${timeoutField.value} seconds`
              : 'Maximum Webhook timeout disabled'}
        </Typography>
      </Box>
      {hasError ? <FormHelperText>{timeoutMeta.error}</FormHelperText> : null}
    </FormControl>
  );
};

const MAX_RETRY_UNIVERSAL = 5;

const RetryConfigField = () => {
  const [enabledField, , enabledHelpers] = useField<boolean>('retryEnabled');
  const [maxRetryField, maxRetryMeta, maxRetryHelpers] = useField<number>('maxRetry');

  const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
    enabledHelpers.setValue(event.target.checked);
  };

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    const numericValue = Array.isArray(value) ? value[0] : value;
    maxRetryHelpers.setValue(Math.round(numericValue));
    maxRetryHelpers.setTouched(true, true);
  };

  const hasError = enabledField.value && !!maxRetryMeta.error;
  const sliderValue = maxRetryField.value ?? 3;

  return (
    <FormControl fullWidth error={hasError} sx={{ mt: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabledField.value}
            onChange={handleToggle}
          />
        }
        label="Enable Payment Retry"
      />
      <Box sx={{ pl: 4, pt: 1 }}>
        <Slider
          min={1}
          max={MAX_RETRY_UNIVERSAL}
          step={1}
          marks={[
            { value: 1, label: '1' },
            { value: MAX_RETRY_UNIVERSAL, label: `${MAX_RETRY_UNIVERSAL}` },
          ]}
          value={enabledField.value ? sliderValue : 1}
          disabled={!enabledField.value}
          valueLabelDisplay="auto"
          onChange={handleSliderChange}
        />
        <Typography variant="caption" color="text.secondary">
          {enabledField.value
            ? `Max retries allowed: ${sliderValue} (universal max: ${MAX_RETRY_UNIVERSAL})`
            : 'Enable retry to configure max retries'}
        </Typography>
      </Box>
      {hasError ? <FormHelperText>{maxRetryMeta.error}</FormHelperText> : null}
    </FormControl>
  );
};

export default function AssetEdit({
  assetId,
  onClose,
}: {
  assetId?: string;
  onClose?: () => void;
}) {
  const { assetId: routeAssetId } = useParams<{ assetId: string }>();
  const effectiveAssetId = assetId || routeAssetId;
  const role = useSelector((state: RootState) => state.user.role);
  const isAdmin = role === UserRole.ADMIN;
  const { amcId } = useAMC();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !effectiveAssetId;

  const { data: assets } = useGetAssetsQuery(amcId!);
  const [createAsset] = useCreateAssetMutation();
  const [updateAsset] = useUpdateAssetMutation();
  const { data: _suggestedIps } = useGetIpSuggestionsQuery(effectiveAssetId!, {
    skip: !effectiveAssetId,
  });

  // Find the existing asset and convert it to the Asset type from assetApi
  let existingAsset = assets?.find((asset) => asset.id === effectiveAssetId);
  if (existingAsset) {
    // Add required fields that might be missing in the amcApi.Asset interface
    const assetWithRequiredFields = {
      ...removeNullOrUndefined(existingAsset),
      apiKey: existingAsset.apiKey || '',
      webhookTimeout: existingAsset.webhookTimeout ?? 0,
      upiTimeout: toUpiTimeoutNumber(existingAsset.upiTimeout),
      retryEnabled: existingAsset.retryEnabled ?? false,
      maxRetry: existingAsset.maxRetry ?? 3,
    };
    existingAsset = assetWithRequiredFields as Asset;
  }

  const formInitialValues: FormValues = existingAsset
    ? (() => {
      const { amc: _amc, id: _id, webhookTimeout, ...rest } = existingAsset;
      const sanitizedTimeout = webhookTimeout && webhookTimeout >= 1 ? Math.min(webhookTimeout, 60) : 1;
      const hasWebhookUrl = !!rest.webhookUrl;
      return {
        ...rest,
        webhookTimeout: sanitizedTimeout,
        webhookTimeoutEnabled: !!webhookTimeout && webhookTimeout > 0 && hasWebhookUrl,
      };
    })()
    : initialValues;

  const handleSubmit = async (values: FormValues) => {
    try {
      const { webhookTimeoutEnabled, webhookTimeout, upiTimeout, ...restValues } = values;
      const payload = {
        ...restValues,
        webhookTimeout: webhookTimeoutEnabled ? webhookTimeout ?? 1 : 0,
        upiTimeout: toUpiTimeoutNumber(upiTimeout),
      };

      if (isNew) {
        await createAsset({
          ...payload,
          apiKey: payload.apiKey || Math.random().toString(36).substring(2, 15), // Generate a random API key if not provided
          amc: { id: amcId! },
        }).unwrap();
      } else {
        await updateAsset({
          ...payload,
          id: effectiveAssetId!,
          amc: { id: amcId! },
        }).unwrap();
      }
      enqueueSnackbar('Asset saved successfully', { variant: 'success' });
      navigate(`/amcs/${amcId}/assets`);
      onClose?.();
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to save asset', { variant: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 4,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
              <FormikTextField name="name" label="Name" fullWidth />
              {!isNew ? <FormikTextField name="apiKey" label="API Key" fullWidth /> : <></>}
              <FormikTextField name="webhookUrl" label="Webhook URL" fullWidth />
              <WebhookTimeoutField />
              <UpiTimeoutField />
              <FormikTextField name="callbackSuccess" label="Success Callback URL" fullWidth />
              <FormikTextField name="callbackFailure" label="Failure Callback URL" fullWidth />
              {isAdmin && <RetryConfigField />}
              <FormikTextField name="proxyUrl" label="Proxy URL" fullWidth />
              <FormikTextField name="allowedIps" label="Allowed IP Addresses" fullWidth />
              <Typography variant="body2" color="text.secondary">
                Recent IPs: {Array.isArray(_suggestedIps) && _suggestedIps.length > 0
                  ? _suggestedIps.join(", ") : "No Suggestions"}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormikSwitch name="insertDtr" label="Housekeeping" />
                <FormikSwitch name="insertAotm" label="Insert Mandate" />
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <FormikTextField
                multiline
                rows={15}
                name="upiAutoPayPendingHtml"
                label="UPI Autopay Pending HTML"
                fullWidth
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: isMobile ? 2 : 0,
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{ borderRadius: '20px', width: isMobile ? '100%' : 'auto' }}
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  disableElevation
                  variant="contained"
                  sx={{ borderRadius: '20px', width: isMobile ? '100%' : 'auto' }}
                >
                  {isNew ? 'Create Asset' : 'Update Asset'}
                </FormikSubmitButton>
              </Box>
            </Box>
          </Box>
        </Form>
      </Formik>
    </Box>
  );
}
